import React, { useCallback, useMemo, useState } from 'react'
import { RefreshCw, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { RecommendationsCard } from '../../../components/RecommendationsCard/RecommendationsCard'
import { PerformanceDashboardCard } from '../../../components/PerformanceDashboard'
import {
  demoResetSummary,
  type ActionSummaryData,
} from '../../../services/recommendations_api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/authentication/useAuth'
import useApiService from '../../../services/api'
import ReactSelectWrapper from '../../../components/FormUtils/ReactSelectWrapper'
import type { SingleValue } from 'react-select'
import { useHideFooter } from '../../../context/layout'
import { NewFooter } from '../../../components/Layout/Footer/NewFooter'
import { ErrorBoundary } from '../../../components/UI/ErrorBoundary'

/**
 * Home page - Your daily recommendations and priorities
 *
 * This is the main landing page where users start their day.
 * Shows AI-generated recommendations with prioritized deals, emails, and campaigns.
 *
 * Superusers can select any user to view/generate their recommendations.
 */
export function HomePage() {
  useHideFooter()
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>()
  const queryClient = useQueryClient()
  const { user, isAdmin, isLimited } = useAuth()
  const { data: users } = useApiService().useGetUsers(
    (!user?.is_superuser && !isAdmin) || isLimited
  )

  const isSuperuser = user?.is_superuser
  const canManageSummaries = isSuperuser || isAdmin

  const summaryQueryKey = useMemo(
    () => ['recommendations', selectedUserId ?? 'self'],
    [selectedUserId]
  )

  const handleDemoReset = async () => {
    setIsGenerating(true)
    try {
      const newSummary = await demoResetSummary(selectedUserId)
      // Response contains the complete summary — write directly into cache,
      // no polling needed since the reset is synchronous.
      queryClient.setQueryData(summaryQueryKey, newSummary)
    } catch (error) {
      console.error('Failed to reset demo:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSummaryLoaded = useCallback(
    (summary: ActionSummaryData | null) => {
      if (summary?.status === 'COMPLETED' || summary?.status === 'FAILED') {
        setIsGenerating(false)
      }
    },
    []
  )

  const userOptions = useMemo(() => {
    const others =
      users?.map((u) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })) ?? []
    // Superusers are excluded from the users/ endpoint — prepend self so they
    // can select their own summary from the dropdown
    if (user && !others.find((o) => o.value === user.id)) {
      others.unshift({
        value: user.id,
        label: `${user.first_name} ${user.last_name} (${user.email}) (me)`,
      })
    }
    return others
  }, [users, user])
  const selectedUser =
    users?.find((u) => u.id === selectedUserId) ??
    (selectedUserId === user?.id ? user : undefined)
  const selectedUserOption = userOptions.find(
    (option) => option.value === selectedUserId
  )

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Good morning!</h1>
            <p className="mt-2 text-lg text-gray-600">
              Here&apos;s what needs your attention today
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Admin user selector */}
            {canManageSummaries && users && (
              <ReactSelectWrapper
                className="min-w-[240px]"
                options={userOptions}
                value={selectedUserOption ?? null}
                onChange={(option: unknown) => {
                  const typedOption = option as SingleValue<{
                    label: string
                    value: number
                  }>
                  // Selecting yourself is the same as "My Summary" (no user_id param)
                  const id = typedOption?.value
                  setSelectedUserId(id === user?.id ? undefined : id)
                  setIsGenerating(false)
                }}
                placeholder="My Summary"
                isClearable
              />
            )}

            <Link
              to="/settings/ai"
              className="flex items-center gap-2 px-4 py-2 bg-fb-dark-green text-white rounded-lg hover:bg-fb-dark-green-hover transition-colors"
            >
              <Sparkles size={20} />
              Manage AI Agents
            </Link>
          </div>
        </div>

        {/* Personal Performance Dashboard */}
        <section className="mb-8">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Your Performance
          </h2>
          <PerformanceDashboardCard userId={selectedUserId} />
        </section>

        {/* AI Recommendations */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              AI Recommendations
            </h2>
            {isSuperuser && (
              <button
                type="button"
                onClick={handleDemoReset}
                disabled={isGenerating}
                className="p-1 rounded text-fg-muted hover:text-fg hover:bg-surface-alt disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={
                  selectedUserId
                    ? `Reset demo for ${selectedUser?.email}`
                    : 'Reset demo'
                }
              >
                <RefreshCw
                  size={14}
                  className={isGenerating ? 'animate-spin' : ''}
                />
              </button>
            )}
          </div>
          <ErrorBoundary
            fallback={
              <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                <p className="text-red-800 font-medium">
                  Failed to load recommendations
                </p>
                <p className="mt-1 text-sm text-red-600">
                  Please try refreshing the page
                </p>
              </div>
            }
          >
            <RecommendationsCard
              userId={selectedUserId}
              onSummaryLoaded={handleSummaryLoaded}
            />
          </ErrorBoundary>
        </section>

        <div className="mt-10">
          <NewFooter className="rounded-none px-0" />
        </div>
      </div>
    </div>
  )
}
